import "@arco-design/web-react/es/Upload/style";
import _Upload from "@arco-design/web-react/es/Upload";
import "@arco-design/web-react/es/Button/style";
import _Button from "@arco-design/web-react/es/Button";
import _IconUpload from "@arco-design/web-react/icon/react-icon/IconUpload";
import "@arco-design/web-react/es/Modal/style";
import _Modal from "@arco-design/web-react/es/Modal";
import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
import React, { useState, useRef, useEffect, useCallback } from 'react';
import useLocale from '@/utils/useLocale';
import locale from './locale';
import request from '@/utils/request';
import { validateExcelFile } from '@/utils/import';
import OSS from 'ali-oss';
export function UploadExcel(props) {
  const {
    requiredColumns,
    onSuccess,
    buttonText,
    bizType = 'import',
    disabled,
    statusUrl,
    onImportSuccess,
    onImportError,
    pollingInterval = 2000,
    maxAttempts = 60
  } = props;
  const t = useLocale(locale);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [fileList, setFileList] = useState([]);
  const timerRef = useRef(null);

  // 用 ref 保存回调，避免轮询闭包捕获过期引用
  const onImportSuccessRef = useRef(onImportSuccess);
  const onImportErrorRef = useRef(onImportError);
  useEffect(() => {
    onImportSuccessRef.current = onImportSuccess;
    onImportErrorRef.current = onImportError;
  });

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);
  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setImporting(false);
  }, []);
  const startPolling = useCallback(url => {
    let attempts = 0;
    timerRef.current = setInterval(() => {
      attempts++;
      request.get(url).then(res => {
        const {
          code,
          data
        } = res.data;
        if (code === 200 && data?.status === 'completed') {
          stopPolling();
          _Message.success(t['upload.excel.import.success']);
          onImportSuccessRef.current?.(data.file_key);
        } else if (code === 200 && data?.status === 'failed') {
          stopPolling();
          const errMsg = data.error || data.message || t['upload.excel.import.failed'];
          _Message.error(errMsg);
          onImportErrorRef.current?.(errMsg);
        }
      }).catch(err => {
        stopPolling();
        _Message.error(err.message);
        onImportErrorRef.current?.(err.message);
      });
      if (attempts >= maxAttempts) {
        stopPolling();
        _Message.error(t['upload.excel.import.failed']);
        onImportErrorRef.current?.();
      }
    }, pollingInterval);
  }, [maxAttempts, pollingInterval, stopPolling, t]);
  const fetchStsToken = async () => {
    const res = await request.post('/v1/public/aliyun/oss-sts-token', {
      biz_type: bizType
    });
    const {
      code,
      message,
      data
    } = res.data;
    if (code === 200) {
      return data;
    }
    throw new Error(message);
  };
  const handleChange = async (fileList, fileItem) => {
    // 只处理新选中的文件（status 为 init）
    if (fileItem.status !== 'init' || !fileItem.originFile) return;
    const file = fileItem.originFile;
    if (requiredColumns && requiredColumns.length > 0) {
      const {
        valid,
        errors
      } = await validateExcelFile(file, requiredColumns);
      if (!valid) {
        _Message.error(errors.join('；'));
        setFileList([]);
        return;
      }
    }
    setUploading(true);
    try {
      const stsToken = await fetchStsToken();
      const client = new OSS({
        region: stsToken.region,
        accessKeyId: stsToken.access_key_id,
        accessKeySecret: stsToken.access_key_secret,
        stsToken: stsToken.security_token,
        bucket: stsToken.bucket,
        secure: true,
        refreshSTSToken: async () => {
          const data = await fetchStsToken();
          return {
            accessKeyId: data.access_key_id,
            accessKeySecret: data.access_key_secret,
            stsToken: data.security_token
          };
        },
        refreshSTSTokenInterval: 300000
      });
      const objectKey = `${stsToken?.key_prefix || ''}${file.name}`;
      await client.put(objectKey, file);

      // 通知外部 OSS 上传完成（通常用于触发后端开始异步导入）
      await onSuccess?.(file.name);

      // 若提供了轮询地址，自动开始查询导入状态
      if (statusUrl) {
        const url = statusUrl(file.name);
        setImporting(true);
        _Message.info({
          content: t['upload.excel.importing'],
          duration: 3000
        });
        startPolling(url);
      }
    } catch {
      _Message.error(t['upload.excel.error']);
    } finally {
      setUploading(false);
      setFileList([]);
    }
  };
  const isDisabled = disabled || importing;
  return /*#__PURE__*/React.createElement(React.Fragment, null, importing && /*#__PURE__*/React.createElement(_Modal, {
    title: t['upload.excel.importing'],
    visible: true,
    footer: null,
    closable: false
  }), /*#__PURE__*/React.createElement(_Upload, {
    autoUpload: false,
    accept: ".xls,.xlsx",
    limit: 1,
    fileList: fileList,
    disabled: isDisabled,
    onChange: handleChange
  }, /*#__PURE__*/React.createElement(_Button, {
    icon: /*#__PURE__*/React.createElement(_IconUpload, null),
    loading: uploading,
    disabled: isDisabled
  }, buttonText || t['upload.excel.title'])));
}